import Image from "../gui/Image";
import Modal from "../gui/Modal";
import AbstractDataHandler from "../shared/AbstractDataHandler";
import { CURRENT_VERSION } from "../shared/constants";
import { getBaseUrl, getPlanPath, getPlanSlug, getUsername, makePlantElement } from "../shared/utils";
import { PLACE } from "../tools/actions/ActionHelper";
import { plannerSignInUrl } from "../../lib/planner_auth";
import { parseSaveErrorBody, DEFAULT_SAVE_ERROR } from "../../lib/plan_save_errors";
import { capturePlannerImageUpload } from "../../lib/image_upload";

export default class DataHandler extends AbstractDataHandler {
    redirectAfterSave = false;

    getData() {
        if (this.isNewPlan()) {
            const preload = this.preloadedSpecies();
            let storageData = this.storage.get();

            if (preload && !username) {
                // Guests arriving via "Plan a garden with this plant" always
                // get a fresh plan with just that plant — the stored draft is
                // not restored (and gets replaced by the new plan's auto-save)
                this.loadData(null, null);
                storageData == null ? this.provider.showWelcomeMessage() : null;
                this.provider._checkNewPlanPrompts(false);
                this.placePreloadedPlant(preload);
                return;
            }

            let newData = this.loadData(storageData, storageData);
            storageData == null
                ? this.provider.showWelcomeMessage()
                : (
                    newData.showMigrationMessage
                        ? this.provider.showMigrationMessage() : null
                )
            this.provider._checkNewPlanPrompts(false);
            if (preload) {
                this.prefillSearch(preload);
            }
        } else {
            let uri = `${getBaseUrl(this.p)}/${getPlanSlug(this.p)}/data`;
            this.spinner.showFullscreen();
            this.executeGetRequest(uri, (data) => {
                let newData = this.loadData(data, this.storage.get());
                this.imageMenu.opacitySlider.value(newData.image?.opacity || 255);
                if (newData.showMigrationMessage) {
                    this.provider.showMigrationMessage();
                }
            });
        }
    }

    loadLists(func) {
        let uri = `/${username}/plans/lists`;
        this.spinner.showSidebar();
        this.executeGetRequest(uri, func);
    }

    // Species JSON for /plans/new?plant=<slug>, embedded on the canvas
    // element by the plans controller. Null when not arriving via a plant CTA.
    preloadedSpecies() {
        let species;
        try {
            species = JSON.parse(document.getElementById('p5canvas')?.dataset?.preloadPlant || 'null');
        } catch {
            return null;
        }
        return species?.id ? species : null;
    }

    capturePreload() {
        window.posthog?.capture('planner_plant_preloaded', {
            plant_slug: new URLSearchParams(window.location.search).get('plant'),
            logged_in: !!username,
        });
    }

    // Guests: place the plant once at the canvas center and select it
    placePreloadedPlant(species) {
        this.capturePreload();

        this.provider.speciesCollection.add(species);
        const element = makePlantElement(this.provider, species);

        const layerName = Array.isArray(species.Layer) ? species.Layer[0] : species.Layer;
        const layer = (layerName && this.provider.layers.get(layerName)) || this.provider.activeContextPanel.activeLayer;
        element.layer = layer;
        layer?.addElement(element);

        const centerX = Math.round((this.p.width / 2 - this.provider.view.posX) / this.provider.view.scale);
        const centerY = Math.round((this.p.height / 2 - this.provider.view.posY) / this.provider.view.scale);
        const placed = this.actions.execute(PLACE, [element], centerX, centerY);

        this.provider.tools.setTool('select');
        this.provider.sideBar.setTab('info');
        this.provider.selection.select(placed);
    }

    // Logged-in users keep their draft; the sidebar search is prefilled instead
    prefillSearch(species) {
        this.capturePreload();
        const searchTab = this.getTab('search');
        searchTab.searchInput.value(species.name);
        searchTab.performSearch();
    }

    savePlan(forced = false) {
        if (!forced && !this.unsavedChanges) {
            return;
        }

        if (!this.isNewPlan()) {
            this.updatePlan();
        } else if (!username) {
            this.promptGuestToSignIn();
        } else if (!this.settings.name) {
            this.askForName();
        } else {
            this.createPlan();
        }
    }

    // Guests can build a plan but need an account to persist it. Rather than
    // bouncing them straight to the sign-in page (which reads as a dead end),
    // explain what's happening and reassure them their work is kept — the draft
    // lives in localStorage, so "Keep editing" leaves it intact and they can
    // sign in whenever they're ready.
    promptGuestToSignIn() {
        this.enableHotKeys = false;
        let modal = new Modal(this.provider);
        let dismiss = () => {
            this.enableHotKeys = true;
            modal.remove();
        };
        modal.onClickOutside(dismiss)
            .setTitle('Save your garden plan')
            .addContent(this.p.createElement(
                'p',
                'Create a free account or sign in to save your plan. Your work is kept while you do — it\'ll be here when you get back.'
            ))
            .setButtonText('Sign in to save')
            .onClickButton(() => {
                // Flush the pending local draft and retain planner query context
                // such as a preloaded plant during the authentication handoff.
                this.storage.save();
                location.href = plannerSignInUrl(this.p.getURL());
            })
            .onClickCancel(dismiss);
        modal.cancelButton.html('Keep editing');
    }

    askForName(onDismiss = null) {
        this.enableHotKeys = false;
        let nameModal = new Modal(this.provider);
        let nameInput = this.p.createInput('').class('input-text');
        let clickAction = () => {
            this.settings.name = nameInput.value();
            nameModal.remove();
            this.createPlan();
        }
        nameInput.elt.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                clickAction();
            }
        });
        nameModal.onClickOutside(() => {
            this.enableHotKeys = true;
            nameModal.remove();
            onDismiss?.();
        })
            .setTitle('Add a name')
            .addContent(nameInput)
            .setButtonText('Save')
            .onClickButton(clickAction);
    }

    askToSavePrompt(onDismiss = null) {
        this.enableHotKeys = false;
        let modal = new Modal(this.provider);
        let nameInput = this.p.createInput('').class('input-text');
        let dismiss = () => {
            this.enableHotKeys = true;
            modal.remove();
            onDismiss?.();
        };
        let save = () => {
            this.settings.name = nameInput.value();
            modal.remove();
            // Guests can't persist yet — route them to the sign-in prompt
            // instead of POSTing to an authenticated endpoint. The name they
            // typed stays in the draft for when they return.
            if (!username) {
                this.promptGuestToSignIn();
            } else {
                this.createPlan();
            }
        };
        nameInput.elt.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') save();
        });
        modal.onClickOutside(dismiss)
            .setTitle('Save your plan?')
            .addContent(this.p.createElement('p', 'Give it a name and we\'ll save it for you.'))
            .addContent(nameInput)
            .setButtonText('Save')
            .onClickButton(save)
            .onClickCancel(dismiss);
        modal.cancelButton.html('Not now');
    }

    createPlan(name) {
        let postData = this.getPostData();
        this.createSpinner();
        this.executeSaveRequest(
            '/plans',
            'POST',
            postData,
            (result) => {
                location.href = this.p.getURL().replace('/plans/new', result['uri']);
            });
    }

    updatePlan() {
        let uri = getPlanPath(this.p);
        this.redirectAfterSave = this.settings.name != this.planName;
        if (this.redirectAfterSave) {
            this.createSpinner();
        }
        this.executeSaveRequest(
            uri,
            'PATCH',
            this.getPostData(),
            (result) => {
                if (this.redirectAfterSave) {
                    location.href = this.p.getURL().replace(uri + '/edit', result['uri']);
                }
            });
    }

    deletePlan() {
        let uri = getPlanPath(this.p)
        this.createSpinner();
        this.p.httpDo(
            uri,
            {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': this.csrfToken,
                    'Accept': 'application/json',
                }
            },
            (data) => {
                location.href = this.p.getURL().replace(uri + '/edit', '/' + getUsername(this.p) + '/plans');
            },
            (data) => {
                console.log(JSON.parse(data));
            }
        )
    }

    createSpinner() {
        this.spinner.showFullscreen();
    }

    getPostData() {
        return {
            plan: {
                content: {
                    view: this.view.export(),
                    settings: this.settings.export(),
                    elements: this.elements.export(),
                    layers: this.layers.export(),
                    image: this.image?.export(),
                    version: CURRENT_VERSION,
                },
                name: this.settings.name,
                access: this.settings.access,
            }
        };
    }

    updateImage(file) {
        this.image = null;
        const formData = new FormData();
        formData.append('image[file]', file.file);

        let uri = getPlanPath(this.p) + '/image/background_image';

        fetch(uri, {
            method: 'POST',
            headers: {
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
            body: formData,
        })
            .then(async (response) => {
                if (!response.ok) {
                    // The server rejected the file (wrong format or too large).
                    // Show its validation message and clear the spinner instead
                    // of leaving the upload control stuck loading.
                    const body = await response.json().catch(() => null);
                    const { message } = parseSaveErrorBody(body, response.status);
                    this.captureImageUpload('rejected', file, response.status);
                    this.imageMenu.loadingFinished(false);
                    this.showSaveErrorModal(message, false, 'We could not add that image');
                    return;
                }
                const data = await response.json();
                this.captureImageUpload('success', file, response.status);
                this.image = new Image(this.provider, data, true);
            })
            .catch(error => {
                console.error(error);
                this.captureImageUpload('error', file);
                this.imageMenu.loadingFinished(false);
                this.showSaveErrorModal(DEFAULT_SAVE_ERROR, false, 'We could not add that image');
            });
    }

    captureImageUpload(result, file, status = null) {
        capturePlannerImageUpload({
            plannerVersion: 'classic',
            result,
            fileType: file?.file?.type,
            fileSizeBytes: file?.file?.size,
            status,
        });
    }

    uploadOverview(blob) {
        if (this.isNewPlan()) return;

        const formData = new FormData();
        formData.append('image[file]', blob, 'overview.png');

        let uri = getPlanPath(this.p) + '/image/overview';

        fetch(uri, {
            method: 'POST',
            headers: {
                'X-CSRF-Token': this.p.select('meta[name="csrf-token"]').elt.content,
                'Accept': 'application/json',
            },
            body: formData,
        })
            .catch(error => {
                console.error(error);
            });
    }

    deleteImage(id) {
        fetch(`/plans/image/${id}`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-Token': this.csrfToken,
                'Accept': 'application/json',
            },
        })
            .catch(error => {
                console.error(error);
            });
    }


    isNewPlan() {
        return this.p.getURLPath()[1] == 'new';
    }
};
